import { SourceType } from './source'
import { Offset } from './onyx_server.type'

import { SearchFilter } from '@/types/filter.type'
import { FunctionMode, MessageSender, SelectableModel } from '@/types/message.type'

export type OptionalReferenceType =
  | 'earning_events_summary'
  | 'earning_events_transcript'
  | 'earning_events_first_take'

export interface OptionalReference {
  type: OptionalReferenceType
  keys: string[]
}

export enum ReferenceDocumentType {
  EarningsCall = 'earnings_call',
  SellSide = 'sell_side',
  SecFiling10K = '10-K',
  SecFiling10Q = '10-Q',
  Slides = 'slides',
  UploadedFile = 'uploaded_file',
}

export interface Reference {
  display?: unknown
  id: string
  idx: number
  content: string
  file_title: string
  ticker: string
  doc_type: ReferenceDocumentType
  doc_date: string
  doc_id: string
  origin_presigned_url?: string
  created_at: string
  updated_at: string
  event_start_at: string
  published_at: string
  reference_type: ReferenceType
  news_date: string
  news_position: number
  doc_public_url: string
  brokerage_name: string
  news_url?: string
  user_source_key?: string
  user_source_text_file_key?: string
  user_source_url?: string
  user_source_type?: SourceType
  offset?: Offset[]
}

export interface NewsReference {
  id: string
  position: number
  origin_source_url: string
  snippet: string
  source: string
  title: string
  news_date: string
  created_at: string
  updated_at: string
}

export interface SearchReferencesParams {
  question: string
  search_type: SelectableModel | undefined
  conversation_id?: string | undefined
  filters?: SearchFilter
}

export interface IntegrateSearchReferencesParams {
  conversation_id: string
  parent_message_id?: string
  selected_model: SelectableModel
  content: string
  sender: MessageSender
  start_time?: number
  function_mode?: FunctionMode
  filters?: SearchFilter
  is_follow_up_question?: boolean
  optional_references?: OptionalReference[]
}

export interface SearchResultParams {
  history_id: string
}

export interface CreateReferenceParams {
  message_id: string
  chunk: string
}

export enum SearchStatus {
  Ready = 'ready',
  Ongoing = 'ongoing',
  Finished = 'finished',
  Failed = 'failed',
  Error = 'ERROR',
  NetworkError = 'network_error',
}

export enum TranslateStatus {
  Ready = 'ready',
  Ongoing = 'ongoing',
  Finished = 'finished',
  Failed = 'failed',
  Error = 'ERROR',
  NetworkError = 'network_error',
}

export enum ReferenceType {
  Document = 'document',
  News = 'news',
  UploadedFile = 'uploaded_file',
  FileSearch = 'file_search',
}

export enum ReferenceMode {
  Search = 'search',
  News = 'news',
  IntegrateSearch = 'integrate_search',
}

export enum DocSourceOptionValues {
  // Conference = 'conference',
  // Earnings = 'earnings',
  Transcript = 'transcript',
  Filing = 'filing',
  News = 'news',
  SellSide = 'sell_side',
  IRSlide = 'ir_slide',
}

export const DocSourceOptionLabels: { [key in DocSourceOptionValues]: string } = {
  [DocSourceOptionValues.Filing]: 'Filings',
  [DocSourceOptionValues.News]: 'News',
  [DocSourceOptionValues.Transcript]: 'Transcript',
  [DocSourceOptionValues.SellSide]: 'Broker Report',
  [DocSourceOptionValues.IRSlide]: 'IR Slides',
  // [DocSourceOptionValues.Earnings]: 'Earnings call',
  // [DocSourceOptionValues.Conference]: 'Conference call',
}

export const convertDocumentTypeReadable = (docType: ReferenceDocumentType) => {
  switch (docType) {
    case ReferenceDocumentType.EarningsCall:
      return 'Call Transcript'
    case ReferenceDocumentType.SecFiling10K:
      return '10-K'
    case ReferenceDocumentType.SecFiling10Q:
      return '10-Q'
    case ReferenceDocumentType.Slides:
      return 'IR Slides'
    case ReferenceDocumentType.SellSide:
      return 'Broker Report'
    case ReferenceDocumentType.UploadedFile:
      return 'Attachment'
    default:
      return ''
  }
}

export const SOURCES_OPTIONS: { value: string; label: string }[] = [
  {
    value: DocSourceOptionValues.Transcript,
    label: DocSourceOptionLabels[DocSourceOptionValues.Transcript],
  },
  {
    value: DocSourceOptionValues.Filing,
    label: DocSourceOptionLabels[DocSourceOptionValues.Filing],
  },
  { value: DocSourceOptionValues.News, label: DocSourceOptionLabels[DocSourceOptionValues.News] },
  // {
  //   value: DocSourceOptionValues.SellSide,
  //   label: DocSourceOptionLabels[DocSourceOptionValues.SellSide],
  // },
]

export const ANALYTICS_SOURCES_OPTIONS: { value: string; label: string }[] = [
  ...SOURCES_OPTIONS,
  {
    value: DocSourceOptionValues.IRSlide,
    label: DocSourceOptionLabels[DocSourceOptionValues.IRSlide],
  },
]

// Sub-rows under Transcript in the Analytics Sources panel. `value` is what
// `filter_transcript_types` carries. Each stands for one
// `stock_documents.transcript_type` (earnings_call_transcript /
// conference_call_transcript); the DPC filter-scope block does that mapping.
export const TRANSCRIPT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'earnings_call', label: 'Earnings Calls' },
  { value: 'conference', label: 'Conferences & Events' },
]

/**
 * Data vendors selectable in the Sources panel. Deselecting one
 * blocks that vendor's MCP tools server-side (analytics-server vendor policy
 * hook) and nudges the agent to re-plan on the vendor that is still on.
 * `value` must match the analytics-server vendor buckets ('snp' / 'factset' /
 * 'third_bridge' / 'lseg' / 'visible_alpha').
 */
export const ANALYTICS_DATA_VENDOR_OPTIONS: { value: string; label: string }[] = [
  { value: 'snp', label: 'S&P Capital IQ' },
  { value: 'factset', label: 'FactSet' },
  { value: 'third_bridge', label: 'Third Bridge' },
  { value: 'lseg', label: 'LSEG' },
  { value: 'visible_alpha', label: 'Visible Alpha' },
]

// The vendors that existed before Third Bridge and LSEG. `filter_data_vendors` only ever
// names these, because older bundles still send it and analytics-server reads it that way.
export const LEGACY_DATA_VENDOR_VALUES = ['snp', 'factset']

/**
 * Which vendors existed when a persisted `filter_metadata.data_vendors` was written. Each
 * entry adds the vendors one release introduced and the marker that release appends to
 * the saved list. A list without a marker predates markers.
 *
 * On restore, vendors added after the list's version come back on: the user never had
 * the chance to turn them off. Without this, every vendor added later would reopen as
 * blocked in every conversation saved before it.
 *
 * Adding a vendor: add it to ANALYTICS_DATA_VENDOR_OPTIONS and append
 * `{ marker: '__v4__', vendors: ['new_vendor'] }`. Never change or reuse an existing
 * entry; saved conversations already carry those markers.
 */
export const DATA_VENDOR_VERSIONS: { marker: string | null; vendors: string[] }[] = [
  { marker: null, vendors: LEGACY_DATA_VENDOR_VALUES },
  { marker: '__v2__', vendors: ['third_bridge', 'lseg'] },
  { marker: '__v3__', vendors: ['visible_alpha'] },
]

// Vendors whose tools reach the agent only through a DPC flag. Without it (per
// GET /data_vendors/me) the row stays in the pickers, locked, with a "contact us" note.
export const FLAG_LOCKED_DATA_VENDORS = ['third_bridge', 'lseg', 'visible_alpha'] as const

export const DATA_VENDOR_CONTACT_EMAIL = 'support@linqalpha.com'

// GET /data_vendors/me: whether a flag-gated vendor's tools reach this person's agent.
export interface DataVendorAvailability {
  third_bridge: boolean
  visible_alpha: boolean
  lseg: boolean
}

// One-line descriptions for the Sources panel rows. Kept apart from the option
// lists above because IntegrateSearchFilter shares ANALYTICS_SOURCES_OPTIONS.
// Document-source wording mirrors the DPC filter-scope block the agent reads.
export const ANALYTICS_SOURCE_DESCRIPTIONS: Record<string, string> = {
  [DocSourceOptionValues.Transcript]: 'Earnings calls, conferences and company events',
  earnings_call: 'Quarterly and annual results calls',
  conference: 'Investor conferences, AGMs, investor days and other company events',
  [DocSourceOptionValues.Filing]: 'SEC filings and annual/interim reports',
  [DocSourceOptionValues.News]: 'News articles from our news feeds',
  [DocSourceOptionValues.IRSlide]: 'Investor presentations and IR slides',
  snp: 'Fundamentals and private-company financials',
  factset: 'Fundamentals, consensus estimates, ownership',
  third_bridge: 'Expert interview transcripts',
  lseg: 'Prices, FX, rates, bonds and estimates',
  visible_alpha: 'Consensus estimates for segments and KPIs',
  web_search: 'Search results and pages from the open web',
}

export enum SearchStep {
  URL_PROCESSING = 'url_processing',
  SEARCHING = 'searching',
  READING_SOURCES = 'reading_sources',
  DOCUMENT_SCREENING = 'document_screening',
  CONCLUDING = 'concluding',
  COMPLETED = 'completed',
}

export const SearchStepLabels: Record<SearchStep, string> = {
  [SearchStep.URL_PROCESSING]: 'Processing',
  [SearchStep.SEARCHING]: 'Searching',
  [SearchStep.READING_SOURCES]: 'Reading Sources',
  [SearchStep.DOCUMENT_SCREENING]: 'Screening',
  [SearchStep.CONCLUDING]: 'Concluding...',
  [SearchStep.COMPLETED]: 'Search Step',
}
